/**
 * Composable для AI-чата.
 * Управляет состоянием, отправкой сообщений и SSE-стримингом.
 */

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface PageContext {
  path: string;
  title?: string;
}

const API_URL = process.env.API_URL as string;

export const useAiChat = () => {
  const route = useRoute();

  const messages = ref<ChatMessage[]>([]);
  const isStreaming = ref(false);
  const error = ref<string | null>(null);

  let abortController: AbortController | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  // Таймаут запроса: 30 секунд
  const REQUEST_TIMEOUT_MS = 30000;

  /**
   * История сообщений (без system) для отправки на сервер
   */
  const history = computed(() =>
    messages.value.filter((m) => m.role !== 'system'),
  );

  /**
   * Отправляет сообщение и стримит ответ
   */
  const sendMessage = async (text: string) => {
    if (!text.trim() || isStreaming.value) return;

    // Снимаем историю ДО добавления текущего сообщения,
    // т.к. сервер сам добавляет body.message в конец контекста
    const historySnapshot = history.value;

    // Контекст страницы: path + title (document.title уже локализован через Quasar useMeta)
    const title =
      typeof document !== 'undefined' && document.title ? document.title : undefined;
    const pageContext: PageContext = title
      ? { path: route.path, title }
      : { path: route.path };

    // Добавляем сообщение пользователя
    messages.value.push({ role: 'user', content: text.trim() });

    // Добавляем placeholder для ответа AI
    messages.value.push({ role: 'assistant', content: '' });

    error.value = null;
    isStreaming.value = true;
    abortController = new AbortController();

    // Устанавливаем таймаут
    timeoutId = setTimeout(() => {
      abortController?.abort();
      error.value = 'ERROR_TIMEOUT';
      const lastMsg = messages.value[messages.value.length - 1];
      if (lastMsg?.role === 'assistant' && !lastMsg.content) {
        messages.value.pop();
      }
      isStreaming.value = false;
    }, REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(`${API_URL}/ai-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text.trim(),
          history: historySnapshot,
          pageContext,
        }),
        signal: abortController.signal,
      });

      if (response.status === 429) {
        error.value = 'RATE_LIMITED';
        messages.value.pop(); // убираем placeholder
        isStreaming.value = false;
        return;
      }

      // Различаем типы ошибок по статусу
      if (!response.ok) {
        if (response.status === 503) {
          error.value = 'ERROR_SERVICE_UNAVAILABLE';
        } else if (response.status >= 500) {
          error.value = 'ERROR_API_FAILURE';
        } else {
          error.value = 'ERROR';
        }
        messages.value.pop(); // убираем placeholder
        isStreaming.value = false;
        return;
      }

      // Читаем SSE-стрим
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      const assistantIndex = messages.value.length - 1;
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Парсим SSE события
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // последний элемент может быть неполным

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;

          const data = line.slice(6).trim();
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            if (parsed.content) {
              // Защита: если историю очистили во время стрима — прерываемся
              if (!messages.value[assistantIndex]) {
                stopStreaming();
                return;
              }
              messages.value[assistantIndex].content += parsed.content;
            }
            // Нормализуем ошибку: сервер шлёт коды (STREAM_ERROR),
            // неизвестные коды маппим на 'ERROR' (см. te()-fallback в AiChat)
            if (parsed.error) {
              error.value = typeof parsed.error === 'string' ? parsed.error : 'ERROR';
            }
          } catch {
            // Игнорируем невалидный JSON
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        // Если это был таймаут — ошибка уже установлена
        if (error.value !== 'ERROR_TIMEOUT') {
          // Пользователь отменил вручную — ничего не делаем
        }
      } else if ((err as Error).message?.includes('Failed to fetch') || (err as Error).name === 'TypeError') {
        // Сетевая ошибка
        console.error('[useAiChat] Network error:', err);
        error.value = 'ERROR_NETWORK';
        const lastMsg = messages.value[messages.value.length - 1];
        if (lastMsg?.role === 'assistant' && !lastMsg.content) {
          messages.value.pop();
        }
      } else {
        console.error('[useAiChat] Error:', err);
        error.value = 'ERROR';
        // Убираем placeholder если ответ пустой
        const lastMsg = messages.value[messages.value.length - 1];
        if (lastMsg?.role === 'assistant' && !lastMsg.content) {
          messages.value.pop();
        }
      }
    } finally {
      isStreaming.value = false;
      abortController = null;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    }
  };

  /**
   * Останавливает текущий стрим
   */
  const stopStreaming = () => {
    abortController?.abort();
    isStreaming.value = false;
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  /**
   * Очищает историю сообщений
   */
  const clearHistory = () => {
    // Не даём очищать во время стриминга (запись идёт по индексу)
    if (isStreaming.value) {
      stopStreaming();
    }
    messages.value = [];
    error.value = null;
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return {
    messages,
    isStreaming,
    error,
    sendMessage,
    stopStreaming,
    clearHistory,
  };
};
